import AdminCheckBadgeSvg from '@assets/svg/AdminCheckBadge';
import AdminUserSvg from '@assets/svg/AdminUser';
import { cn, useAdminStore, useAppRouter } from 'shared-components';
import { type FunctionComponent, useEffect, useState } from 'react';

import { type AccountMenuProps } from './AccountMenu.types';
import { IDS } from 'e2e-page-objects/selectors';

const formatTimeLeft = (impersonatedUntil: Date): string => {
  const res = Math.floor((new Date(impersonatedUntil).getTime() - Date.now()) / 1000);

  const minutes = `0${Math.floor(res / 60)}`;
  const seconds = `0${Math.floor(res % 60)}`;

  return `${minutes.substring(minutes.length - 2)}:${seconds.substring(seconds.length - 2)}`;
};

const AccountMenu: FunctionComponent<AccountMenuProps> = ({ dataTestid, className }) => {
  const { navigateTo } = useAppRouter();
  const firstname = useAdminStore((s) => s.user.firstname);
  const lastname = useAdminStore((s) => s.user.lastname);
  const impersonatedUntil = useAdminStore((s) => s.user.impersonatedUntil);

  const [timeLeft, setTimeLeft] = useState<string | null>(null);

  useEffect(() => {
    if (!impersonatedUntil) {
      setTimeLeft(null);
      return;
    }

    const update = () => setTimeLeft(formatTimeLeft(impersonatedUntil));
    update();
    const timer = setInterval(update, 1000);

    return () => clearInterval(timer);
  }, [impersonatedUntil]);

  return (
    <button
      type="button"
      data-testid={dataTestid}
      aria-label={[firstname, lastname].filter(Boolean).join(' ') || undefined}
      className={cn('flex cursor-pointer items-center gap-3 border-0 bg-transparent p-0', className)}
      onClick={() => navigateTo({ name: 'Admin Profile' })}
    >
      {(firstname || lastname) && (
        <span className="hidden text-overline-2 sm:block">
          {firstname && <span className="block text-overline-2">{firstname}</span>}
          {lastname && <span className="block text-overline-2">{lastname}</span>}
        </span>
      )}
      <span className="relative inline-flex">
        <AdminUserSvg className="size-7 shrink-0 text-secondary-900" data-testid="user" />
        <AdminCheckBadgeSvg
          className="absolute -right-0.5 -bottom-0.5 size-4 text-success-600"
          data-testid="logged-in-badge"
        />
        {timeLeft && (
          <span
            data-testid={IDS.impersonationCountdown}
            className="absolute top-5 right-0 z-1000 w-6.25 bg-success-800 text-center text-[0.5rem] text-white"
          >
            {timeLeft}
          </span>
        )}
      </span>
    </button>
  );
};

AccountMenu.displayName = 'AccountMenu';
export { AccountMenu };
